export type PremiumSource = { feature: string; context: string; placement: string };
export type PremiumAttribution = { first: PremiumSource; latest: PremiumSource; lastActivityAt: number };
const SESSION_KEY = 'ss:premium:source';
const CHECKOUT_KEY = 'ss:premium:checkout-source';
const SESSION_TTL = 30 * 60 * 1000;
const CHECKOUT_TTL = 24 * 60 * 60 * 1000;
const features = new Set(['all', 'account', 'themes', 'organizers', 'questions', 'history', 'draw', 'address']);

function validSource(value: unknown): value is PremiumSource {
  if (!value || typeof value !== 'object') return false;
  const source = value as PremiumSource;
  return features.has(source.feature) && [source.context, source.placement].every(
    item => typeof item === 'string' && /^[a-z][a-z0-9_-]{0,63}$/.test(item),
  );
}
function read(key: string, ttl: number): PremiumAttribution | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(key) || 'null') as PremiumAttribution | null;
    if (value && validSource(value.first) && validSource(value.latest) && Number.isFinite(value.lastActivityAt)
      && value.lastActivityAt <= Date.now() && Date.now() - value.lastActivityAt < ttl) return value;
    window.sessionStorage.removeItem(key);
  } catch { /* Missing/blocked storage must not affect checkout or analytics. */ }
  return null;
}
function save(key: string, value: PremiumAttribution): void {
  try { window.sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* Best effort. */ }
}
export function currentPremiumAttribution(paymentReturn = false): PremiumAttribution | null {
  return (paymentReturn ? read(CHECKOUT_KEY, CHECKOUT_TTL) : null) ?? read(SESSION_KEY, SESSION_TTL);
}
export function rememberPremiumSource(source: PremiumSource): PremiumAttribution | null {
  const previous = read(SESSION_KEY, SESSION_TTL);
  if (!validSource(source)) return previous;
  const specific = source.feature !== 'all';
  const value = {
    first: !previous || (previous.first.feature === 'all' && specific) ? source : previous.first,
    latest: !previous || specific ? source : previous.latest,
    lastActivityAt: Date.now(),
  };
  save(SESSION_KEY, value);
  return value;
}
/** Write before window.open: its same-origin blank tab inherits this session snapshot. */
export function rememberPremiumCheckout(source: PremiumSource): void {
  const existing = currentPremiumAttribution();
  const value = rememberPremiumSource(existing ? { ...source, feature: 'all' } : source);
  if (value) save(CHECKOUT_KEY, value);
}
